import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertOctagon, Eye, Sliders, ArrowRight, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

/**
 * Hard Stop Modal
 * 
 * Displayed when a user hits their cost-to-revenue hard stop threshold.
 * This is triggered by USAGE_LIMIT_REACHED error from backend.
 * 
 * Users can still:
 * - View previous analyses
 * - Use cached results
 * - Reclassify deals (adjust criteria)
 */
export default function HardStopModal({ 
  isOpen, 
  onClose, 
  errorData,
  onViewDeals,
  onGoToPricing 
}) {
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  const {
    current_ratio_pct = 0,
    limit_pct = 90,
    plan_type = 'FREE',
    allowed_actions = []
  } = errorData || {};
  
  const handleViewDeals = () => {
    if (onViewDeals) {
      onViewDeals();
    } else {
      navigate('/deals');
    }
    onClose();
  };
  
  const handleUpgrade = () => {
    if (onGoToPricing) {
      onGoToPricing();
    } else {
      navigate('/pricing');
    }
    onClose();
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="hard-stop-modal"
    >
      <Card 
        className="bg-slate-800 border-red-500/30 max-w-md w-full shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <AlertOctagon className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <CardTitle className="text-white text-lg">
                You've reached your analysis limit
              </CardTitle>
              <CardDescription className="text-slate-400">
                {plan_type} plan • {current_ratio_pct.toFixed(1)}% of {limit_pct}% limit
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <p className="text-slate-300 text-sm">
            You can still view your deals and adjust scenarios, but new analyses are temporarily paused.
          </p>
          
          {/* What you can still do */}
          <div className="bg-slate-700/30 rounded-lg p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">
              What you can still do
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Eye className="w-4 h-4 text-emerald-400" />
                <span>View your saved deals and analyses</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Sliders className="w-4 h-4 text-emerald-400" />
                <span>Adjust criteria and reclassify deals</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <ArrowRight className="w-4 h-4 text-emerald-400" />
                <span>Use previously cached property data</span>
              </div>
            </div>
          </div>
          
          {/* Usage bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Cost-to-Revenue</span>
              <span className="text-red-400 font-medium">{current_ratio_pct.toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
                style={{ width: `${Math.min((current_ratio_pct / limit_pct) * 100, 100)}%` }}
              />
            </div>
            <p className="text-xs text-slate-500">
              Limit: {limit_pct}% • Resets next billing cycle
            </p>
          </div>
          
          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              onClick={handleUpgrade}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="hard-stop-upgrade-btn"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Upgrade Plan
            </Button>
            <Button
              variant="outline"
              onClick={handleViewDeals}
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="hard-stop-view-deals-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Saved Deals
            </Button>
          </div>
          
          <button
            onClick={onClose}
            className="w-full text-center text-sm text-slate-500 hover:text-slate-400 pt-2"
          >
            Close
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
